import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Inbox, Images, Wrench, TrendingUp, ArrowRight, Phone, Mail } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_COLORS = {
  New: "bg-green-100 text-green-700",
  "In Progress": "bg-blue-100 text-blue-700",
  Quoted: "bg-amber-100 text-amber-700",
  Won: "bg-emerald-100 text-emerald-700",
  Lost: "bg-slate-200 text-slate-600",
};

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
  });

  if (isLoading) return <p className="text-slate-500">Loading…</p>;

  const stats = [
    { label: "New leads", value: data.new_leads, icon: Inbox, color: "text-green-600 bg-green-50", to: "/admin/leads" },
    { label: "Leads this week", value: data.leads_this_week, icon: TrendingUp, color: "text-blue-600 bg-blue-50", to: "/admin/leads" },
    { label: "Gallery items", value: data.gallery_count, icon: Images, color: "text-amber-600 bg-amber-50", to: "/admin/gallery" },
    { label: "Active services", value: data.active_services, icon: Wrench, color: "text-slate-700 bg-slate-100", to: "/admin/services" },
  ];

  return (
    <div data-testid="dashboard-page">
      <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl">Dashboard</h1>
      <p className="text-slate-500 mt-1">Here's what's happening today.</p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Link to={s.to} key={s.label} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`h-11 w-11 rounded-xl grid place-items-center ${s.color}`}><s.icon className="h-5 w-5" /></div>
            <div className="mt-4 text-3xl font-display font-black text-slate-900">{s.value}</div>
            <div className="text-sm text-slate-500 font-medium">{s.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="font-display font-bold text-slate-900 text-lg">Recent leads</h2>
          <Link to="/admin/leads" className="text-sm font-semibold text-green-700 inline-flex items-center gap-1" data-testid="dashboard-all-leads">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {data.recent_leads.length === 0 ? (
          <p className="p-6 text-slate-500">No leads yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.recent_leads.map((l) => (
              <li key={l.id}>
                <Link to={`/admin/leads/${l.id}`} className="flex items-center justify-between p-4 sm:p-5 hover:bg-slate-50 transition" data-testid={`dashboard-lead-${l.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{l.full_name}</div>
                    <div className="text-sm text-slate-500 flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{l.phone}</span>
                      {l.postcode && <span>{l.postcode}</span>}
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${STATUS_COLORS[l.status] || "bg-slate-100"}`}>{l.status}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
